import React, { useEffect, useRef, useState } from 'react';
import LevelOneCanvas from './LevelOneCanvas';
import { decodeResponses, encodeRequest, SpatialMap, SpatialPlayer, SpatialSnapshot, WorldPosition } from './protocol';
import { BrowserVoice, VoiceCredentials } from './browserVoice';
import { releasingRuntime, waitForRuntimeRelease } from './runtimeLifecycle';
import { MapRoom } from './mapDesign';
import { TimedSpatialSnapshot } from './interpolation';
import { nearbyBreakoutArea } from './roomProximity';
import { supportsIntersectAtlas, useVenueAtlas } from './atlas';
import AtlasToolbar from './AtlasToolbar';

let roomRequester: ((roomId: string, codes: string[]) => Promise<void>) | undefined;
export function requestLevelOneRoom(roomId: string, codes: string[] = []): Promise<void> {
  return roomRequester ? roomRequester(roomId, codes) : Promise.reject(new Error('The 2D world is still connecting.'));
}

export async function levelOneApi(token: string, path: string) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new Error(`Could not join the venue (${response.status}).`);
  return response.json();
}
export async function getSpatialSocketUrl(token: string): Promise<string> {
  const server = await levelOneApi(token, 'current-server');
  const host = String(server.game_server_ip || '');
  const url = new URL(/^wss?:\/\//.test(host) ? host : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${host}`);
  if (server.game_server_port) url.port = String(server.game_server_port);
  if (window.location.protocol === 'https:' && url.protocol !== 'wss:') throw new Error('The venue needs a secure multiplayer connection.');
  return url.href;
}

export interface SpatialHandoff { position: WorldPosition; rotation: WorldPosition }
export async function prepareSpatialHandoff(token: string): Promise<SpatialHandoff> {
  const url = await getSpatialSocketUrl(token);
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(url); ws.binaryType = 'arraybuffer';
    let done = false;
    const finish = (error?: Error, handoff?: SpatialHandoff) => { if (done) return; done = true; clearTimeout(timeout); ws.close(1000); error ? reject(error) : resolve(handoff!); };
    const timeout = setTimeout(() => finish(new Error('This server needs the Level 1 update before you can switch views.')), 15000);
    ws.onopen = () => ws.send(encodeRequest({ action: 'handoff', token }));
    ws.onmessage = event => {
      try { for (const value of decodeResponses(event.data)) {
        const data = value as any;
        if (data.type === 'handoffReady') finish(undefined, data);
        if (data.type === 'error') finish(new Error(data.message));
      } } catch { finish(new Error('The server could not transfer your player.')); }
    };
    ws.onerror = () => finish(new Error('Could not transfer your player. Please try again.'));
    ws.onclose = () => { if (!done) finish(new Error('The transfer connection closed. Please try again.')); };
  });
}

interface Props { accessToken: string; show: any; roomId: string; onReady: () => void; rooms?: MapRoom[] }

export default function LevelOneRuntime({ accessToken, show, onReady, rooms }: Props) {
  const { atlas } = useVenueAtlas(supportsIntersectAtlas(show.upc_world_map_content_url));
  const [overview, setOverview] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [map, setMap] = useState<SpatialMap>({ vertices: [], indices: [] });
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [connected, setConnected] = useState(false);
  const snapshot = useRef<SpatialSnapshot>({ selfId: '', players: [] });
  const snapshotHistory = useRef<TimedSpatialSnapshot[]>([]);
  const socket = useRef<WebSocket>();
  const input = useRef({ x: 0, z: 0, sprint: false });
  const ready = useRef(onReady); ready.current = onReady;
  const welcomed = useRef(false);
  const roomsRef = useRef(rooms || []); roomsRef.current = rooms || [];

  useEffect(() => {
    let stopped = false;
    let voice: BrowserVoice | undefined;
    let timer: ReturnType<typeof setInterval> | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const previousBridge = window.sendMessageToUnity;
    const vertices: WorldPosition[] = [];
    let pendingPlayers: SpatialPlayer[] = [];
    let nearbyArea: string | undefined;
    let pendingRoom: { resolve: () => void; reject: (reason: Error) => void; timeout: ReturnType<typeof setTimeout> } | undefined;
    const requestRoom = (targetRoom: string, codes: string[]) => new Promise<void>((resolve, reject) => {
      const ws = socket.current;
      if (!welcomed.current || ws?.readyState !== WebSocket.OPEN) { reject(new Error('The 2D world is still connecting.')); return; }
      if (pendingRoom) { reject(new Error('Please wait for the current room change.')); return; }
      pendingRoom = { resolve, reject, timeout: setTimeout(() => { pendingRoom = undefined; reject(new Error('The room change timed out.')); }, 20000) };
      ws.send(encodeRequest({ action: 'room', roomId: targetRoom, codes: codes.slice(-20) }));
    });
    roomRequester = requestRoom;
    const bridge = (_object: string, method: string, raw?: string | number) => {
      if (method !== 'ReceivePayload' || typeof raw !== 'string') return;
      try {
        const message = JSON.parse(raw);
        const payload = typeof message.payload === 'string' ? JSON.parse(message.payload) : message.payload;
        if (message.eventType === 'VoiceCommand') voice?.command(payload);
        if (message.eventType === 'PlayerMute' || message.eventType === 'PlayerUnmute') voice?.setPlayerMuted(String(payload.userId), message.eventType === 'PlayerMute');
        if (message.eventType === 'IvsScreenSharePublisher' && socket.current?.readyState === WebSocket.OPEN) socket.current.send(encodeRequest({ action: 'screenShare', participantId: payload.participantId, active: payload.active, roomId: payload.roomId, stageArn: payload.stageArn, sessionId: payload.sessionId }));
        // Room ids go to the server; arbitrary browser-supplied teleport positions do not.
        if (message.eventType === 'Teleport') setError('This action is not available in 2D yet. You can change rooms from the Rooms app.');
      } catch { /* legacy optional bridge payload */ }
    };
    const start = async () => {
      setError(''); setConnected(false); welcomed.current = false;
      const url = await getSpatialSocketUrl(accessToken);
      if (stopped) return;
      const ws = new WebSocket(url); ws.binaryType = 'arraybuffer'; socket.current = ws;
      window.sendMessageToUnity = bridge;
      timeout = setTimeout(() => { if (!welcomed.current) { setError('This multiplayer server could not prepare Level 1. Check the server build and walkable map.'); ws.close(1000); } }, 120000);
      ws.onopen = () => ws.send(encodeRequest({ action: 'join', version: 1, token: accessToken }));
      ws.onmessage = event => {
        if (stopped) return;
        try {
          for (const response of decodeResponses(event.data)) {
            const data = response as any;
            if (data.type === 'error') {
              if (pendingRoom) { clearTimeout(pendingRoom.timeout); pendingRoom.reject(new Error(data.message)); pendingRoom = undefined; }
              else setError(data.message);
              continue;
            }
            if (data.type === 'room' && pendingRoom) { clearTimeout(pendingRoom.timeout); pendingRoom.resolve(); pendingRoom = undefined; }
            if (data.type === 'welcome') {
              welcomed.current = true; snapshot.current.selfId = data.selfId;
              snapshotHistory.current = [];
              if (timeout) clearTimeout(timeout);
              if (data.voice) {
                voice = new BrowserVoice(show.upc_url, data.voice as VoiceCredentials, () => levelOneApi(accessToken, 'events/22222/voice/rtc-token'), Number(show.audible_distance) || 32, Number(show.conversational_distance) || 4);
                const joiningVoice = voice;
                // The map can connect while the retiring Unity heap is released;
                // only Agora must wait to avoid two clients briefly sharing a UID.
                void waitForRuntimeRelease().then(() => joiningVoice.connect());
              }
              timer = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) ws.send(encodeRequest({ action: 'move', ...input.current }));
              }, 100);
            } else if (data.type === 'mapStart') vertices.length = 0;
            else if (data.type === 'mapChunk') vertices.push(...data.vertices);
            else if (data.type === 'mapEnd') {
              setMap({ vertices: [...vertices], indices: vertices.map((_, i) => i) });
              setConnected(true);
              ready.current();
              window.dispatchEvent(new CustomEvent('ClientLoadedWorldAndReady', { detail: '{}' }));
            } else if (data.type === 'snapshotStart') pendingPlayers = [];
            else if (data.type === 'snapshotChunk') pendingPlayers.push(...data.players);
            else if (data.type === 'snapshotEnd') {
              snapshot.current = { selfId: snapshot.current.selfId, players: pendingPlayers };
              snapshotHistory.current.push({ ...snapshot.current, receivedAt: performance.now() });
              if (snapshotHistory.current.length > 8) snapshotHistory.current.splice(0, snapshotHistory.current.length - 8);
              voice?.update(snapshot.current);
              const self = pendingPlayers.find(player => player.userId === snapshot.current.selfId);
              const nextArea = self ? nearbyBreakoutArea(self.position, roomsRef.current) : undefined;
              if (nextArea !== nearbyArea) {
                if (nearbyArea) window.dispatchEvent(new CustomEvent('AreaTriggerEntered', { detail: JSON.stringify({ playerId: snapshot.current.selfId, areaId: nearbyArea, entered: false }) }));
                nearbyArea = nextArea;
                if (nearbyArea) window.dispatchEvent(new CustomEvent('AreaTriggerEntered', { detail: JSON.stringify({ playerId: snapshot.current.selfId, areaId: nearbyArea, entered: true }) }));
              }
            } else if (data.type === 'area') {
              window.dispatchEvent(new CustomEvent('AreaTriggerEntered', { detail: JSON.stringify(data) }));
            }
          }
        } catch { setError('The multiplayer server sent an incompatible Level 1 update.'); ws.close(1000); }
      };
      ws.onerror = () => { if (!stopped) setError('Could not connect to the multiplayer server.'); };
      ws.onclose = () => {
        welcomed.current = false;
        if (!stopped) setConnected(false);
        if (timer) clearInterval(timer);
        void voice?.close();
        if (!stopped) setError(previous => previous || 'You disconnected from the venue. Reconnect to continue.');
      };
    };
    void start().catch(reason => { if (!stopped) setError(reason instanceof Error ? reason.message : 'Could not open Level 1.'); });
    return () => {
      stopped = true; welcomed.current = false;
      if (roomRequester === requestRoom) roomRequester = undefined;
      if (pendingRoom) { clearTimeout(pendingRoom.timeout); pendingRoom.reject(new Error('The view changed.')); }
      if (timer) clearInterval(timer); if (timeout) clearTimeout(timeout);
      snapshotHistory.current = [];
      socket.current?.close(1000); releasingRuntime(voice?.close() || Promise.resolve());
      if (window.sendMessageToUnity === bridge) window.sendMessageToUnity = previousBridge;
    };
  }, [accessToken, show, attempt]);

  return <>
    <LevelOneCanvas map={map} atlas={atlas} overview={overview} zoom={zoom} snapshotHistory={snapshotHistory} rooms={rooms} onMove={(x, z, sprint) => { input.current = { x, z, sprint }; }} faceDistance={Number(show.audible_distance) || 32} />
    {atlas && connected && <AtlasToolbar overview={overview} zoom={zoom} onOverview={setOverview} onZoom={setZoom} />}
    {!connected && !error && <div role="status" className="absolute inset-0 flex items-center justify-center bg-slate-950 text-white">Preparing the 2D venue…</div>}
    {error && <div role="alert" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', background: '#16263e', padding: 24, borderRadius: 16, color: 'white', zIndex: 11000, maxWidth: 440 }}>
      <p>{error}</p><button className="mt-4 rounded bg-indigo-600 px-4 py-2" onClick={() => connected ? setError('') : setAttempt(value => value + 1)}>{connected ? 'Dismiss' : 'Reconnect'}</button>
    </div>}
  </>;
}
